/**
 * voice-v8 — Werkzeugkarten: Gedächtnis: Suchen, Merken, Löschen, Stammdaten, Chat-Verlauf.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { absatz, baueKarte, kartenTitel, leerHinweis, wertZeile } from "../karte.js";
import { relativeZeit } from "../relative-time.js";
import { trimText, unwrapResult } from "./hilfe.js";

/**
 * 2026-07-29 (Martin: "Jetzt müssen wir mal die Karte in ein Format bringen,
 * die passt"): Das Merken einer Stammdate zeigte den technischen Rückgabewert
 * — SCHLUESSEL / ERSETZT: false / ANZAHL: 11. Für den Nutzer bedeutungslos:
 * "false" sagt nichts, "11" ist eine Zahl ohne Bezug, und WAS gemerkt wurde,
 * stand gar nicht da.
 *
 * Die Karte zeigt jetzt das, was zählt: die Tatsache selbst. Darunter, klein,
 * woher sie stammt — denn genau das macht eine Stammdate später überprüfbar.
 */
export function buildStammdatenCard(tool: string, result: unknown): HTMLElement {
  const r = unwrapResult(result) as {
    ok?: boolean;
    error?: string;
    schluessel?: string;
    wert?: string;
    quelle?: string;
    ersetzt?: boolean;
    anzahl?: number;
    geloescht?: string;
  };
  const el = baueKarte("stammdaten");
  const loeschen = tool === "stammdaten_vergessen";

  if (r.ok !== true) {
    el.appendChild(kartenTitel("Nicht gemerkt"));
    const p = document.createElement("p");
    p.className = "tw-hinweis";
    p.textContent = r.error || "Hat nicht geklappt.";
    el.appendChild(p);
    return el;
  }

  if (loeschen) {
    el.appendChild(kartenTitel("Vergessen"));
    const p = document.createElement("p");
    p.className = "tw-stammdaten-wert";
    p.textContent = r.geloescht ?? "";
    el.appendChild(p);
    return el;
  }

  el.appendChild(kartenTitel(r.ersetzt ? "Aktualisiert" : "Gemerkt"));

  // Die Tatsache selbst — das ist die Botschaft.
  const wert = document.createElement("p");
  wert.className = "tw-stammdaten-wert";
  wert.textContent = r.wert ?? "";
  el.appendChild(wert);

  // Worunter es abgelegt ist — leise, aber auffindbar.
  if (r.schluessel) {
    const k = document.createElement("p");
    k.className = "tw-stammdaten-key";
    k.textContent = r.schluessel;
    el.appendChild(k);
  }

  // Die Quelle macht die Angabe überprüfbar.
  if (r.quelle) {
    const q = document.createElement("p");
    q.className = "tw-stammdaten-quelle";
    q.textContent = r.quelle;
    el.appendChild(q);
  }
  return el;
}

interface MemorySearchResult {
  items?: Array<{
    type: string;
    content: string;
    score: number;
    tags: string[];
  }>;
}

export function buildMemorySearchCard(result: unknown): HTMLElement {
  const r = (result ?? {}) as MemorySearchResult;
  const el = baueKarte("memory-search");
  el.appendChild(kartenTitel(`Gedächtnis · ${r.items?.length ?? 0} Treffer`));

  const list = document.createElement("ul");
  list.className = "tool-memory-results";
  for (const item of (r.items ?? []).slice(0, 5)) {
    const li = document.createElement("li");
    const score = document.createElement("span");
    score.className = "tool-memory-score";
    // 15.10.2026: ein Eintrag ohne Zahl oder Text ließ die ganze Karte abstürzen (gesehen beim Bau jeder Karte mit Fremdfeldern).
    score.textContent = typeof item.score === "number" ? item.score.toFixed(2) : "";
    const txt = document.createElement("span");
    txt.className = "tool-memory-content";
    txt.textContent = typeof item.content === "string" ? item.content.slice(0, 200) : "";
    li.appendChild(score);
    li.appendChild(txt);
    list.appendChild(li);
  }
  el.appendChild(list);
  return el;
}

interface MemorySaveResult {
  id?: string;
  saved?: boolean;
  // Pre.381: content + type + tags damit User sieht WAS gespeichert wurde.
  content?: string;
  type?: string;
  tags?: string[];
  linkedToProject?: string | null;
  /** Ticket 2497: gemeldeter Widerspruch — dann wurde NICHTS gespeichert. */
  kind?: string;
}

/**
 * Die Art der Erinnerung, auf Deutsch. (22.09.2026, Ticket 2497)
 *
 * ⚠️ Hier stand `r.type` roh in der Karte: „fact", „preference", „note",
 *    „contact". Das sind die INTERNEN Namen — englisch, mitten in einer
 *    sonst deutschen Oberflaeche. Albert hat das gemeldet, und es ist
 *    dieselbe Bauart wie „Sperrliste englisch, Antwort deutsch" vom 18.09.
 */
const ART_NAMEN: Record<string, string> = {
  fact: "Tatsache",
  preference: "Vorliebe",
  note: "Notiz",
  contact: "Kontakt",
  resonance: "Resonanz",
  chat: "Gespräch",
  project_chapter: "Kapitel",
};

export function buildMemorySaveCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as unknown as MemorySaveResult;
  const el = baueKarte("memory-save");
  /*
   * ⚠️⚠️ 22.09.2026, TICKET 2497 — HIER STAND IMMER „Im Gedächtnis
   * gespeichert", EGAL WAS ZURUECKKAM.
   *
   * Meldet `memory_save` einen Widerspruch, ist `saved: false` und es
   * wurde NICHTS abgelegt. Die Karte hat trotzdem gemeldet, es sei
   * gespeichert. Albert las also „gespeichert" und schloss daraus, das
   * System speichere den Wert „zwanghaft trotzdem" — die Anzeige hat einen
   * Fehler behauptet, den es so gar nicht gab, und den echten (die fehlende
   * Rueckfrage) verdeckt.
   *
   * ⚠️ Dieselbe Klasse wie „Faehigkeit da, Anzeige fehlt" — nur andersherum:
   *    die Anzeige behauptet etwas, das nicht geschehen ist.
   */
  const widerspruch = r.kind === "memory_conflict";
  const gespeichert = !widerspruch && r.saved !== false;
  el.appendChild(
    kartenTitel(
      widerspruch
        ? "Widerspruch — noch nichts gespeichert"
        : gespeichert
          ? "Im Gedächtnis gespeichert"
          : "Nicht gespeichert",
    ),
  );
  if (r.content) {
    const body = document.createElement("blockquote");
    body.className = "tool-memory-content";
    // 15.10.2026: eine Notiz als Zeile, nicht als Absatz — die Karte bestätigt, sie ist keine Zweitschrift.
    body.textContent = `„${trimText(String(r.content), 300)}"`;
    el.appendChild(body);
  }
  const metaParts: string[] = [];
  // ⚠️ Ticket 2497: uebersetzt statt roh — siehe ART_NAMEN.
  if (r.type) metaParts.push(ART_NAMEN[r.type] ?? r.type);
  if (Array.isArray(r.tags) && r.tags.length > 0) {
    metaParts.push(r.tags.join(", "));
  }
  if (r.linkedToProject) metaParts.push(`Projekt: ${r.linkedToProject}`);
  if (metaParts.length > 0) {
    const meta = document.createElement("p");
    meta.className = "tool-memory-meta";
    meta.textContent = metaParts.join(" · ");
    el.appendChild(meta);
  }
  return el;
}

interface MemoryDeleteResult {
  ok?: boolean;
  id?: string;
  deleted?: boolean;
  error?: string;
}

export function buildMemoryDeleteCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as unknown as MemoryDeleteResult;
  const el = baueKarte("memory-delete");
  const isOk = r.ok !== false && r.deleted !== false;
  el.appendChild(
    kartenTitel(isOk ? "Erinnerung gelöscht" : "Löschen fehlgeschlagen"),
  );
  if (r.id) wertZeile(el, "ID", r.id.slice(0, 12) + "…");
  if (r.error) {
        el.appendChild(absatz(String(r.error)));
  }
  return el;
}

// Pre.397: chat_history_search-Card — Suchtreffer aus Chatverlauf
interface ChatHistorySearchResult {
  ok?: boolean;
  query?: string;
  count?: number;
  hits?: Array<{
    role?: string;
    content?: string;
    score?: number;
    when?: string;
  }>;
  error?: string;
}

export function buildChatHistorySearchCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as unknown as ChatHistorySearchResult;
  const el = baueKarte("chat-search");
  if (r.ok === false) {
    el.appendChild(kartenTitel("Chatverlauf-Suche — Fehler"));
    const e = absatz(String(r.error ?? "Unbekannter Fehler"), "warn");
    el.appendChild(e);
    return el;
  }
  const anzahl = r.count ?? 0;
  el.appendChild(
    kartenTitel(
      `${anzahl} ${anzahl === 1 ? "Treffer" : "Treffer"} im Chatverlauf · „${trimText(String(r.query ?? "").trim(), 42)}"`,
    ),
  );
  const hits = Array.isArray(r.hits) ? r.hits : [];
  if (hits.length === 0) {
    leerHinweis(el, "Nichts dazu im Chatverlauf gefunden.");
    return el;
  }
  const list = document.createElement("ul");
  list.className = "tool-chat-hits";
  for (const h of hits.slice(0, 5)) {
    const li = document.createElement("li");
    li.className = `tool-chat-hit tool-chat-hit--${h.role ?? "unknown"}`;
    const top = document.createElement("div");
    top.className = "tool-chat-hit-top";
    const role = document.createElement("span");
    role.className = "tool-chat-hit-role";
    role.textContent =
      h.role === "user" ? "Du" : h.role === "assistant" ? "Bot" : "?";
    top.appendChild(role);
    /*
     * ⚠️ Die Trefferguete (64 %) steht nicht mehr in der Zeile.
     *
     * Martin, 30.08.: *„das kann auch viel schoener formatiert werden als
     * so."* In seinem Bild stand die Prozentzahl an der zweitwichtigsten
     * Stelle — direkt neben „Du" — und sagt ihm nichts: Er sucht nicht nach
     * der Bewertung eines Treffers, er sucht die Stelle. Die Zahl ist eine
     * Innensicht der Suche.
     *
     * Weggeworfen wird sie nicht: als Titel bleibt sie erreichbar, wenn man
     * einmal wissen will, warum ein Treffer weit oben steht.
     */
    if (typeof h.score === "number") {
      li.title = `Trefferguete ${Math.round(h.score * 100)} %`;
    }
    if (h.when) {
      const w = document.createElement("span");
      w.className = "tool-chat-hit-when";
      try {
        // Pre.459: SQLite-Stempel "YYYY-MM-DD HH:MM:SS" IST UTC, hat
        // aber kein "Z" → ohne Hilfe interpretiert JS das als Lokalzeit
        // und verschiebt um die TZ-Offset (CEST = +2h falsch).
        // Fix: bei DB-Format explizit als UTC parsen.
        const iso = h.when.includes("T")
          ? h.when
          : h.when.replace(" ", "T") + "Z";
        const d = new Date(iso);
        /*
         * Eine Zeitangabe je Zeile, nicht zwei.
         *
         * Vorher stand hier immer „30. Aug., 08:42" — auch fuer etwas von
         * vor zehn Minuten. Beim Ueberfliegen zaehlt, WIE LANGE her; das
         * Datum hilft erst, wenn es nicht mehr heute war. Dieselbe
         * Entscheidung wie auf der Offen-Seite.
         */
        const alter = Date.now() - d.getTime();
        w.textContent =
          alter >= 0 && alter < 86_400_000
            ? relativeZeit(iso)
            : d.toLocaleDateString("de-DE", {
                day: "numeric",
                month: "short",
                year:
                  d.getFullYear() === new Date().getFullYear()
                    ? undefined
                    : "numeric",
              });
      } catch {
        w.textContent = h.when;
      }
      top.appendChild(w);
    }
    li.appendChild(top);
    const body = document.createElement("p");
    body.className = "tool-chat-hit-body";
    /*
     * Auszug statt ganzer Nachricht. In Martins Bild fuellte EIN Bot-Treffer
     * die halbe Karte — samt fuenf aneinandergereihter Entschuldigungen. Ein
     * Suchergebnis soll die Stelle zeigen, nicht sie ersetzen; wer mehr will,
     * scrollt im Verlauf dorthin.
     */
    body.textContent = trimText(String(h.content ?? "").replace(/\s+/g, " ").trim(), 190);
    li.appendChild(body);
    list.appendChild(li);
  }
  el.appendChild(list);
  return el;
}
